import { CTAButton } from '@gamma/ui/buttons';
import { Input } from '@gamma/ui/form-fields';
import { ErrorBoundary } from '@gamma/ui/overlay';
import {
  CardPageBackButton,
  CardPageBody,
  CardPageCTA,
  CardPageHeading,
  CardPageSubtitle,
} from '@gamma/ui/pages';
import axios, { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import validator from 'validator';
import { CONSTANTS, ROUTES } from '../../../../shared/constants';
import { i18n } from '../../../../shared/localization';

// Configuration constants
const DOMAIN_VALIDATION_TIMEOUT_MS = 10000; // 10 seconds for domain validation
const COOKIE_EXPIRY_DAYS = 30;
const MAX_DOMAIN_LENGTH = 253; // RFC 1035 specification

/**
 * Interface for SSO domain form data
 */
interface SSODomainForm {
  domain: string;
}

/**
 * SSO Login component for domain-based authentication
 * Handles SSO domain validation and redirects to identity provider
 *
 * @component
 * @returns {JSX.Element} SSO login form
 */
export const SSOLogin = () => {
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const validationAbortRef = useRef<AbortController | null>(null);

  const { title, subTitle, button, domain_required, invalid_domain } =
    i18n.pages.login.domain;
  const navigate = useNavigate();
  const routerLocation = useLocation();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    clearErrors,
  } = useForm<SSODomainForm>({
    defaultValues: {
      domain: Cookies.get('sso_domain') ?? '',
    },
    mode: 'onSubmit',
  });

  /**
   * Validates domain format and length
   * @param {string} domain - Domain to validate
   * @returns {boolean} Whether domain is valid
   */
  const isValidDomain = useCallback((domain: string): boolean => {
    if (!domain || domain.length > MAX_DOMAIN_LENGTH) {
      return false;
    }

    // Check for valid FQDN format
    if (!validator.isFQDN(domain)) {
      return false;
    }

    // Additional checks for common issues
    if (domain.includes('http://') || domain.includes('https://')) {
      return false;
    }

    // Check for invalid characters
    if (!/^[a-zA-Z0-9.-]+$/.test(domain)) {
      return false;
    }

    return true;
  }, []);

  /**
   * Validates domain with backend service
   * @async
   * @param {string} domain - Domain to validate with backend
   * @returns {Promise<boolean>} Whether domain is valid
   */
  const validateDomainWithBackend = useCallback(
    async (domain: string): Promise<boolean> => {
      // Abort any existing validation
      if (validationAbortRef.current) {
        validationAbortRef.current.abort();
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      validationAbortRef.current = abortController;

      try {
        const response = await axios({
          method: 'GET',
          url: `/verifydomain`,
          params: { domain },
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: DOMAIN_VALIDATION_TIMEOUT_MS,
          signal: abortController.signal,
        });

        // Check for successful validation
        return response.status === 200;
      } catch (error) {
        const axiosError = error as AxiosError;

        if (axiosError.code === 'ECONNABORTED') {
          console.error('[SSOLogin] Domain validation timeout');
          throw new Error('Domain validation timed out. Please try again.');
        }

        if (axiosError.response?.status === 404) {
          throw new Error('Domain not found or not configured for SSO');
        }

        if (axiosError.response?.status === 403) {
          throw new Error('Domain access denied');
        }

        // Network error
        if (!axiosError.response) {
          throw new Error('Network error. Please check your connection.');
        }

        throw new Error(invalid_domain);
      } finally {
        // Clear abort controller reference
        if (validationAbortRef.current === abortController) {
          validationAbortRef.current = null;
        }
      }
    },
    [invalid_domain],
  );

  /**
   * Handles form submission for SSO domain validation
   * Validates domain format, checks with backend, and redirects to IdP
   *
   * @async
   * @param {SSODomainForm} formData - Form data containing domain
   */
  const onSubmit = useCallback(
    async (formData: SSODomainForm) => {
      const sso_domain = formData.domain.trim().toLowerCase();

      // Clear any previous errors
      clearErrors();

      // Validate domain format
      if (!isValidDomain(sso_domain)) {
        setError('domain', {
          type: 'validation',
          message: invalid_domain,
        });
        return;
      }

      setIsValidating(true);

      try {
        // Validate domain with backend
        const isValid = await validateDomainWithBackend(sso_domain);

        if (!isValid) {
          setError('domain', {
            type: 'validation',
            message: invalid_domain,
          });
          return;
        }

        // Store domain in cookie and session storage
        Cookies.set('sso_domain', sso_domain, {
          expires: COOKIE_EXPIRY_DAYS,
          secure: window.location.protocol === 'https:',
          sameSite: 'lax',
        });
        window.sessionStorage.setItem('sso_domain', sso_domain);

        // Store next URL if present
        const urlParams = new URLSearchParams(routerLocation.search);
        const next = urlParams.get('next');
        if (next) {
          localStorage.setItem('next', next);
        }

        // Build OAuth authorization URL
        const {
          SSO: { domain, client_id, redirect_uri },
        } = CONSTANTS;

        const authParams = new URLSearchParams({
          idp_identifier: sso_domain,
          redirect_uri,
          response_type: 'code',
          client_id,
          scope: 'aws.cognito.signin.user.admin email openid profile',
        });

        const authUrl = `https://${domain}/oauth2/authorize?${authParams.toString()}`;

        // Redirect to identity provider
        window.location.href = authUrl;
      } catch (error) {
        console.error('[SSOLogin] Error during SSO login:', error);

        const errorMessage =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred. Please try again.';

        setError('domain', {
          type: 'validation',
          message: errorMessage,
        });

        setIsValidating(false);
      }
    },
    [
      clearErrors,
      invalid_domain,
      isValidDomain,
      routerLocation.search,
      setError,
      validateDomainWithBackend,
    ],
  );

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      // Abort any pending validation requests
      if (validationAbortRef.current) {
        validationAbortRef.current.abort();
      }
    };
  }, []);

  return (
    <ErrorBoundary page="SSO Login Page">
      <CardPageBody
        as="form"
        data-testid="sso-login-card"
        id="domainForm"
        onSubmit={handleSubmit(onSubmit)}
      >
        <CardPageBackButton
          onClick={() => {
            // Abort any pending validation
            if (validationAbortRef.current) {
              validationAbortRef.current.abort();
            }

            navigate(`${ROUTES.login}${routerLocation.search}`, {
              replace: true,
            });
          }}
        />
        <CardPageHeading>{title}</CardPageHeading>
        <CardPageSubtitle textStyle="body-md">{subTitle}</CardPageSubtitle>
        <Input
          label="Domain"
          placeholder="domain.com"
          isRequired
          isReadOnly={isValidating}
          error={errors?.domain?.message?.toString() ?? ''}
          w="full"
          {...register('domain', {
            required: domain_required,
            maxLength: {
              value: MAX_DOMAIN_LENGTH,
              message: `Domain must be less than ${MAX_DOMAIN_LENGTH} characters`,
            },
            pattern: {
              value: /^[a-zA-Z0-9.-]+$/,
              message:
                'Domain can only contain letters, numbers, dots, and hyphens',
            },
          })}
        />
      </CardPageBody>
      <CardPageCTA>
        <CTAButton
          data-testid="sso-continue-cta"
          type="submit"
          form="domainForm"
          isLoading={isValidating}
          disabled={isValidating}
        >
          {button}
        </CTAButton>
      </CardPageCTA>
    </ErrorBoundary>
  );
};
